/**
 * v1.5 P3：小红书聚光 API 配置页 — admin/director 配 OAuth 凭证 + 查授权状态
 *
 * 流程：
 *   ① 选投流账号 → 填 appId/secret/advertiserId → 保存（secret 加密入库，UI 显示 ****）
 *   ② 程伟把回调地址 https://crm.huanke.org.cn/api/marketing/xhs-oauth-callback 填到聚光后台
 *   ③ 点"去授权" → 跳转聚光 → 程伟确认 → 聚光回调 → access_token + refresh_token 加密存库
 *   ④ Cron 每日 03:00 自动拉花费 + 笔记报表；手动按钮可补拉指定日期范围
 *
 *   P2/P4 待对齐文档：authorize URL 实际跳转 + sync 真调聚光 API
 *   当前 P3：保存/读取/状态 全通；OAuth 跳转返 pendingP2 提示
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message, Select, Input, DatePicker, Tag, Modal } from 'antd';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { $, api } from '../../lib/api';
import { PageHeader, HomeCard, Btn } from '../../components/ui';
// ux9 日期统一出口：授权/同步时间到秒用 fmtDateTimeSec，token 过期到分用 fmtDateTime
import { fmtDateTime, fmtDateTimeSec } from '../../lib/format';

interface AccountListItem {
  id: number; accountName: string; advertiserId?: string | null;
}

interface ConfigStatus {
  accountId: number;
  accountName: string;
  advertiserId?: string | null;
  appId?: string | null;
  hasSecret: boolean;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  tokenExpiresAt?: string | null;
  refreshExpiresAt?: string | null;
  authorizedAt?: string | null;
  lastSyncAt?: string | null;
  lastSyncError?: string | null;
  callbackUrl: string;
}

export function XhsOAuthConfigPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountId, setAccountId] = useState<number | null>(null);

  // 表单状态
  const [appId, setAppId] = useState('');
  const [secret, setSecret] = useState('');  // 空 = 不改；'****' = 占位显示已存
  const [advertiserId, setAdvertiserId] = useState('');

  // 同步日期范围
  const [syncFrom, setSyncFrom] = useState<dayjs.Dayjs>(dayjs().subtract(7, 'day'));
  const [syncTo, setSyncTo] = useState<dayjs.Dayjs>(dayjs().subtract(1, 'day'));

  // 回调 query 参数（OAuth 回跳后弹提示）
  useEffect(() => {
    const ok = searchParams.get('ok');
    const pendingP2 = searchParams.get('pendingP2');
    const err = searchParams.get('err');
    const acc = searchParams.get('account');
    if (acc) setAccountId(Number(acc));
    if (ok) message.success('授权回调成功');
    if (pendingP2) {
      message.warning('授权回调已收到（auth_code 已存）— 等程伟发聚光 OAuth 文档后接入真换 token 调用 (P2)');
    }
    if (err) message.error(`授权失败：${err}`);
    if (ok || pendingP2 || err) {
      // 清掉 query，避免刷新重复弹
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // 投流账号列表
  const { data: accounts } = useQuery<AccountListItem[]>({
    queryKey: ['marketing-accounts'],
    queryFn: () => $.get('/marketing/accounts'),
  });

  // 默认选第一个账号
  useEffect(() => {
    if (!accountId && accounts && accounts.length > 0) {
      setAccountId(Number(accounts[0].id));
    }
  }, [accounts, accountId]);

  // 当前账号配置状态
  const { data: status } = useQuery<ConfigStatus>({
    queryKey: ['xhs-oauth-config', accountId],
    queryFn: () => $.get(`/marketing/xhs-oauth/config/${accountId}`),
    enabled: !!accountId,
  });

  // status 加载后回填表单
  useEffect(() => {
    if (status) {
      setAppId(status.appId ?? '');
      setSecret(status.hasSecret ? '****' : '');
      setAdvertiserId(status.advertiserId ?? '');
    }
  }, [status]);

  // 保存凭证
  const saveCredentials = useMutation({
    mutationFn: () => $.put(`/marketing/xhs-oauth/config/${accountId}`, {
      appId: appId.trim() || undefined,
      // secret = '****' 表示用户没改（沿用 DB 旧值），不发送
      secret: secret === '****' || secret === '' ? undefined : secret.trim(),
      advertiserId: advertiserId.trim() || undefined,
    }),
    onSuccess: () => {
      message.success('凭证已保存（secret 已加密入库）');
      qc.invalidateQueries({ queryKey: ['xhs-oauth-config', accountId] });
      // 保存后清掉 secret 输入框，UI 重新显示 ****
      setSecret('****');
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '保存失败'),
  });

  // 去授权 → 跳转聚光
  const authorize = useMutation({
    mutationFn: () => api.post(`/marketing/xhs-oauth/authorize/${accountId}`).then(r => r.data.data ?? r.data),
    onSuccess: (r: any) => {
      const url = r?.url;
      if (!url) { message.error('授权 URL 生成失败'); return; }
      // 跳转聚光授权页
      window.location.href = url;
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '生成授权 URL 失败'),
  });

  // 手动同步（v1.5 增强：后端分批 30 天/批，大范围自动循环）
  const sync = useMutation({
    mutationFn: (range?: { from: string; to: string }) =>
      api.post(`/marketing/xhs-oauth/sync/${accountId}`, {
        from: range?.from ?? syncFrom.format('YYYY-MM-DD'),
        to: range?.to ?? syncTo.format('YYYY-MM-DD'),
      }, {
        // 同步多批可能耗时较长（每月 ~10-30 秒，4 个月 ~2-3 分钟）
        timeout: 10 * 60 * 1000,  // 10 分钟兜底
      }).then(r => r.data?.data ?? r.data),
    onSuccess: (r: any) => {
      const acc = r?.account ?? {};
      const note = r?.note ?? {};
      const summary = `account ${acc.rows ?? 0} 行 ¥${Number(acc.fee ?? 0).toFixed(0)} | note ${note.rows ?? 0} 行 ¥${Number(note.fee ?? 0).toFixed(0)}`;
      if (r?.failed_batches > 0) {
        message.warning(`部分失败：${r.ok_batches}/${r.batches} 批成功；${summary}`, 8);
      } else {
        message.success(`✅ 同步完成（${r?.batches ?? 1} 批）${summary}`, 6);
      }
      qc.invalidateQueries({ queryKey: ['xhs-oauth-config', accountId] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '同步失败'),
  });

  // v1.5：一键同步全部历史（默认 2026-02-01 ~ 昨天；让程伟一次性补齐 39 万累计花费）
  const syncAllHistory = () => {
    const from = '2026-02-01';
    const to = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const months = dayjs(to).diff(dayjs(from), 'month') + 1;
    Modal.confirm({
      title: '同步全部历史',
      content: (
        <div>
          <div>将同步 <b>{from} ~ {to}</b>（约 {months} 个月）</div>
          <div style={{ marginTop: 8, color: 'var(--text-3)', fontSize: 12 }}>
            • 后端按 30 天/批分批拉取，预计耗时 1-5 分钟<br />
            • 期间请保持页面不关闭<br />
            • 完成后可在「投流对账」和「笔记排行榜」看完整数据
          </div>
        </div>
      ),
      okText: '开始同步',
      cancelText: '取消',
      // ux1 防重：onOk 返回 Promise(mutateAsync) → AntD Modal.confirm 自动给确认钮加 spinner 并禁重复确认；
      // sync mutation 已有 onError toast，不会静默失败
      onOk: () => sync.mutateAsync({ from, to }),
    });
  };

  // 计算 token 剩余时间文字
  const tokenRemainingText = (expiresAt?: string | null) => {
    if (!expiresAt) return null;
    const diff = dayjs(expiresAt).diff(dayjs(), 'minute');
    if (diff < 0) return <Tag color="red">已过期</Tag>;
    if (diff < 60) return <Tag color="orange">剩余 {diff} 分钟</Tag>;
    if (diff < 60 * 24) return <Tag color="blue">剩余 {Math.floor(diff / 60)}h {diff % 60}m</Tag>;
    return <Tag color="green">剩余 {Math.floor(diff / 60 / 24)} 天</Tag>;
  };

  const copyCallback = () => {
    if (!status?.callbackUrl) return;
    navigator.clipboard.writeText(status.callbackUrl).then(
      () => message.success('回调地址已复制'),
      () => message.warning('复制失败，请手动选中'),
    );
  };

  return (
    <>
      <PageHeader
        breadcrumb={['投流', '聚光 API 配置']}
        title="🔗 小红书聚光 API 配置"
        subtitle="OAuth 授权 + 自动拉花费 + 笔记报表（替代手填）"
      />
      <div className="work-content">
        {/* 滚动交给 .work-content(全站统一规则:flex:1+overflow-y:auto)；本页布局(居中/限宽/纵向堆叠/间距)收进内层 div，
            不再把 display:flex+flex-direction:column 压到滚动容器上(那会让 .work-content 既当 work-main 的 flex 子项又当列容器、
            内容超高时不滚动而被裁) */}
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* 账号选择 */}
        <HomeCard title="选择投流账号">
          <Select
            style={{ width: '100%', maxWidth: 480 }}
            value={accountId ?? undefined}
            placeholder="选择要配置聚光 API 的投流账号"
            options={(accounts ?? []).map(a => ({
              value: Number(a.id),
              label: `${a.accountName}（advertiser_id: ${a.advertiserId ?? '未填'}）`,
            }))}
            onChange={(v: number) => setAccountId(v)}
          />
        </HomeCard>

        {accountId && status && (
          <>
            {/* ① 基础凭证 */}
            <HomeCard title="① 基础凭证（程伟在聚光后台获取）">
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 12, columnGap: 12, alignItems: 'center' }}>
                <label style={{ fontSize: 13, color: 'var(--text-2)' }}>App ID</label>
                <Input value={appId} onChange={e => setAppId(e.target.value)} placeholder="聚光后台「我的应用」拿" />

                <label style={{ fontSize: 13, color: 'var(--text-2)' }}>App Secret</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Input.Password
                    value={secret}
                    onChange={e => setSecret(e.target.value)}
                    placeholder={status.hasSecret ? '已存（重填可覆盖）' : '聚光后台「我的应用」拿'}
                    style={{ flex: 1 }}
                  />
                  {status.hasSecret && <Tag color="green">已存（加密）</Tag>}
                </div>

                <label style={{ fontSize: 13, color: 'var(--text-2)' }}>Advertiser ID</label>
                <Input value={advertiserId} onChange={e => setAdvertiserId(e.target.value)} placeholder="聚光账户 ID（程伟手填或授权后接口拉）" />

                <label style={{ fontSize: 13, color: 'var(--text-2)' }}>回调地址</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Input value={status.callbackUrl} readOnly style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }} />
                  <Btn kind="default" onClick={copyCallback}>📋 复制</Btn>
                </div>
                <div />
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  ⚠️ 程伟把上面这个地址粘到【聚光后台 → 我的应用 → 回调地址】，必须完全一致
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <Btn kind="primary" onClick={() => saveCredentials.mutate()}
                     disabled={saveCredentials.isPending}>
                  💾 保存凭证
                </Btn>
              </div>
            </HomeCard>

            {/* ② 授权状态 */}
            <HomeCard title="② 授权状态">
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 8, columnGap: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>状态：</span>
                <span>
                  {status.hasAccessToken && status.authorizedAt
                    ? <Tag color="green">✅ 已授权</Tag>
                    : <Tag color="default">⏳ 未授权</Tag>}
                </span>
                {status.authorizedAt && (
                  <>
                    <span style={{ fontSize: 13, color: 'var(--text-2)' }}>授权时间：</span>
                    <span style={{ fontSize: 13 }}>{fmtDateTimeSec(status.authorizedAt)}</span>
                  </>
                )}
                {status.tokenExpiresAt && (
                  <>
                    <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Access Token 过期：</span>
                    <span style={{ fontSize: 13 }}>
                      {fmtDateTime(status.tokenExpiresAt)} {tokenRemainingText(status.tokenExpiresAt)}
                    </span>
                  </>
                )}
                {status.refreshExpiresAt && (
                  <>
                    <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Refresh Token 过期：</span>
                    <span style={{ fontSize: 13 }}>
                      {fmtDateTime(status.refreshExpiresAt)} {tokenRemainingText(status.refreshExpiresAt)}
                    </span>
                  </>
                )}
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* 聚光 token 死了(410016)也要能重走 OAuth：本按钮【始终渲染】(不分已授权/失效)；
                    「重新授权」(已授权过·含 token 失效 hasAccessToken=true)恒可点；仅【首次去授权】(无 token)才要求先存 App Secret。 */}
                <Btn kind="primary" onClick={() => authorize.mutate()}
                     disabled={authorize.isPending || (!status.hasSecret && !status.hasAccessToken)}>
                  🔗 {status.hasAccessToken ? '重新授权' : '去授权'}
                </Btn>
                {!status.hasSecret && (
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>请先保存基础凭证</span>
                )}
              </div>
            </HomeCard>

            {/* ③ 数据同步 */}
            <HomeCard title="③ 数据同步">
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 8, columnGap: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>上次同步：</span>
                <span style={{ fontSize: 13 }}>
                  {status.lastSyncAt
                    ? <>
                        {fmtDateTimeSec(status.lastSyncAt)}
                        {status.lastSyncError
                          ? <Tag color="red" style={{ marginLeft: 8 }}>❌ {status.lastSyncError}</Tag>
                          : <Tag color="green" style={{ marginLeft: 8 }}>✓ 成功</Tag>}
                      </>
                    : <span style={{ color: 'var(--text-3)' }}>从未同步</span>}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>自动同步：</span>
                <span style={{ fontSize: 13 }}>
                  <Tag color="blue">每天 03:00 拉昨日数据 (cron)</Tag>
                  <span style={{ marginLeft: 8, color: 'var(--text-3)', fontSize: 12 }}>
                    {status.hasAccessToken ? '✅ 已启用' : '⏳ 需先完成授权'}
                  </span>
                </span>
              </div>

              <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-2)', borderRadius: 6 }}>
                <div style={{ fontSize: 13, marginBottom: 8, fontWeight: 500 }}>手动同步</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <DatePicker value={syncFrom} onChange={v => v && setSyncFrom(v)}
                              format="YYYY-MM-DD"
                              disabledDate={d => !d.isBefore(dayjs().startOf('day'))} />
                  <span style={{ color: 'var(--text-3)' }}>~</span>
                  <DatePicker value={syncTo} onChange={v => v && setSyncTo(v)}
                              format="YYYY-MM-DD"
                              // v1.5：聚光离线报表 T-1，结束日期最大昨天
                              disabledDate={d => !d.isBefore(dayjs().startOf('day'))} />
                  <Btn kind="primary" onClick={() => sync.mutate(undefined)}
                       disabled={sync.isPending || !status.hasAccessToken}>
                    {sync.isPending ? '⏳ 同步中…' : '⚡ 立即同步'}
                  </Btn>
                  <Btn kind="default" onClick={syncAllHistory}
                       disabled={sync.isPending || !status.hasAccessToken}>
                    📜 同步全部历史（2/1~昨天）
                  </Btn>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>
                  💡 聚光离线报表 T-1 数据，最新只能到昨天（今天数据次日 03:00 cron 自动拉）
                  <br />
                  💡 大范围同步会按 30 天/批分批拉取，每批 10-30 秒；4 个月约 1-3 分钟
                </div>
              </div>
            </HomeCard>

            {/* ④ 说明 */}
            <HomeCard title="④ 实施进度">
              <ul style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--text-2)', paddingLeft: 20 }}>
                <li>✅ P1 - DB schema + Entity（凭证字段加密 + 笔记日报表）</li>
                <li>✅ P2 - OAuth 真换 token + refresh 自动续期 + advertiser_id 自动存</li>
                <li>✅ P3 - 配置页 UI + OAuth state CSRF 防御 + 保存/读取</li>
                <li>✅ P4 - XhsApiClient + cron 03:00 拉花费 + 笔记报表入库 + 笔记排行榜</li>
              </ul>
            </HomeCard>
          </>
        )}
        </div>
      </div>
    </>
  );
}
